import { Epic } from "../../epics"
import { combineEpics, ofType } from "redux-observable"
import { catchError, flatMap, switchMap } from "rxjs/operators"
import { concat, EMPTY, from, of } from "rxjs"

import * as actions from "./actions"
import { addErrorNotification } from "../notifications/actions"
import {
    initializeBookmarkButton,
    initializeBookmarks,
} from "../bookmarks/actions"
import { RequestCancelledError } from "../../errors"
import { Labels } from "../../util/Translations"
import { ILinkedDocument } from "../inlineattachments"

export const defaultRequestItemBoxDataErrorMessage =
    "Something went wrong when fetching the items."
export const defaultUpdatingAttachmentDescriptionErrorMessage =
    "Something went wrong while trying to update the description."
export const defaultRequestContextMenuErrorMessage =
    "Something went wrong while trying to get the context menu."
export const defaultExecuteContextMenuActionErrorMessage =
    "Something went wrong while trying to execute this action."

const defaultPageSize = 25

function getSelectionFilterId(
    isSmartList: boolean,
    collectionId: number,
): string {
    return `${collectionId}${isSmartList ? "f" : "l"}`
}

export const initializeEpic: Epic = (action$, _state$, _dependencies$) =>
    action$.pipe(
        ofType(actions.INITIALIZE),
        flatMap(() => of(actions.requestData())),
    )

export const reloadEpic: Epic = (
    action$,
    state$,
    { cardView: { itemboxAPI } },
) =>
    action$.pipe(
        ofType(actions.RELOAD),
        switchMap(() => {
            const {
                cardView: {
                    collection: { collectionId, isSmartList },
                    paging: { currentPage },
                },
            } = state$.value
            const selectionFilterId = getSelectionFilterId(
                isSmartList,
                collectionId,
            )
            const smartListContext = isSmartList ? collectionId : -1

            const pageSize = currentPage * defaultPageSize

            return from(
                itemboxAPI.getCardViewData(
                    selectionFilterId,
                    0,
                    smartListContext,
                    pageSize,
                ),
            ).pipe(
                flatMap((data) => {
                    return of(actions.replaceData(data))
                }),
                catchError((error) => {
                    if (error instanceof RequestCancelledError) {
                        return EMPTY
                    }
                    console.error(error)
                    const errorData = {
                        allowAdd: false,
                        items: [],
                        paging: {
                            currentPage: 0,
                            totalNumberOfItems: 0,
                            pageSize: 0,
                        },
                    }
                    return concat(
                        of(actions.receiveData(errorData)),
                        of(
                            addErrorNotification(
                                defaultRequestItemBoxDataErrorMessage,
                            ),
                        ),
                    )
                }),
            )
        }),
    )

export const requestDataEpic: Epic = (
    action$,
    state$,
    { cardView: { itemboxAPI } },
) => {
    return action$.pipe(
        ofType(actions.REQUEST_DATA),
        switchMap(() => {
            const {
                cardView: {
                    collection: { collectionId, isSmartList },
                    paging: { currentPage },
                },
            } = state$.value
            const selectionFilterId = getSelectionFilterId(
                isSmartList,
                collectionId,
            )
            const smartListContext = isSmartList ? collectionId : -1

            return from(
                itemboxAPI.getCardViewData(
                    selectionFilterId,
                    currentPage,
                    smartListContext,
                    defaultPageSize,
                ),
            ).pipe(
                flatMap((data) => {
                    return of(actions.receiveData(data))
                }),
                catchError((error) => {
                    if (error instanceof RequestCancelledError) {
                        return EMPTY
                    }
                    console.error(error)
                    const errorData = {
                        allowAdd: false,
                        items: [],
                        paging: {
                            currentPage: 0,
                            totalNumberOfItems: 0,
                            pageSize: 0,
                        },
                    }
                    return concat(
                        of(actions.receiveData(errorData)),
                        of(
                            addErrorNotification(
                                defaultRequestItemBoxDataErrorMessage,
                            ),
                        ),
                    )
                }),
            )
        }),
    )
}

export const loadMoreEpic: Epic = (action$, _state$, _dependencies$) =>
    action$.pipe(
        ofType(actions.LOAD_MORE),
        flatMap(() => of(actions.requestData())),
    )

export const showContextMenuEpic: Epic = (
    action$,
    _state$,
    { cardView: { itemboxAPI, windowLocation } },
) =>
    action$.pipe(
        ofType(actions.SHOW_CONTEXT_MENU),
        flatMap(({ itemId }: actions.ItemAction) =>
            from(itemboxAPI.getCardViewItemActions(itemId)).pipe(
                flatMap((data) => {
                    return concat(
                        of(actions.receiveContextMenu(itemId, data)),
                        of(initializeBookmarks()),
                        of(
                            initializeBookmarkButton(
                                "CARDVIEW_BOOKMARK_BUTTON",
                                null,
                                "Item",
                                itemId,
                                0,
                                null,
                                `${windowLocation.pathname}/${itemId}`,
                            ),
                        ),
                    )
                }),
                catchError((error: unknown) => {
                    if (error instanceof RequestCancelledError) {
                        return EMPTY
                    }
                    console.error(error)
                    return of(
                        addErrorNotification(
                            defaultRequestContextMenuErrorMessage,
                        ),
                    )
                }),
            ),
        ),
    )

export const executeContextMenuItemActionEpic: Epic = (
    action$,
    _state$,
    { cardView: { itemboxAPI } },
) =>
    action$.pipe(
        ofType(actions.EXECUTE_CONTEXTMENU_ACTION),
        flatMap(({ itemId, button }: actions.ContextMenuCallAction) => {
            let action: string
            const { buttonData } = button
            if (buttonData.actionId == "ExecuteScriptAction") {
                const { callData } = buttonData
                action = `script${callData["scriptId"]}_${callData["listScriptId"]}`
            } else {
                action = buttonData.actionId
            }
            return from(
                itemboxAPI.executeCardViewItemAction(itemId, action),
            ).pipe(
                flatMap((data) => {
                    eval(data)
                    return of(actions.hideContextMenu(itemId))
                }),
                catchError((error) => {
                    if (error instanceof RequestCancelledError) {
                        return EMPTY
                    }
                    console.error(error)
                    return of(
                        addErrorNotification(
                            error.message ||
                                defaultExecuteContextMenuActionErrorMessage,
                        ),
                    )
                }),
            )
        }),
    )

export const refreshItemsEpic: Epic = (
    actions$,
    state$,
    { cardView: { itemboxAPI } },
) =>
    actions$.pipe(
        ofType(actions.REFRESH_ITEMS),
        flatMap(({ itemIds }: actions.ItemsAction) => {
            const {
                cardView: {
                    collection: { collectionId, isSmartList },
                },
            } = state$.value
            const selectionFilterId = getSelectionFilterId(
                isSmartList,
                collectionId,
            )
            return from(
                itemboxAPI.getCardViewItems(selectionFilterId, itemIds),
            ).pipe(
                flatMap((items) => of(actions.receiveItems(items))),
                catchError((error) => {
                    if (error instanceof RequestCancelledError) {
                        return EMPTY
                    }
                    return of(
                        addErrorNotification(
                            error.statusText || error.message || error,
                        ),
                    )
                }),
            )
        }),
    )

export const requestAttachmentsEpic: Epic = (
    action$,
    _state$,
    { cardView: { attachmentsAPI } },
) =>
    action$.pipe(
        ofType(actions.REQUEST_ATTACHMENTS),
        flatMap(({ itemId }: actions.RequestAttachments) =>
            from(attachmentsAPI.getAttachments(itemId, false)).pipe(
                flatMap((linkedDocuments: ILinkedDocument[]) =>
                    of(actions.receiveAttachments(itemId, linkedDocuments)),
                ),
                catchError((error) => {
                    if (error instanceof RequestCancelledError) {
                        return EMPTY
                    }
                    return concat(
                        of(
                            addErrorNotification(
                                error.statusText || error.message || error,
                            ),
                        ),
                        of(actions.receiveAttachments(itemId, [])),
                    )
                }),
            ),
        ),
    )

export const requestAddAttachmentEpic: Epic = (
    action$,
    _state$,
    { cardView: { attachmentsAPI } },
) =>
    action$.pipe(
        ofType(actions.REQUEST_ADD_ATTACHMENT),
        flatMap((action: actions.RequestAddAttachment) =>
            from(
                attachmentsAPI.addAttachment(action.itemId, action.formData),
            ).pipe(
                flatMap(() =>
                    of(actions.requestAttachments(action.itemId, false)),
                ),
                catchError((error) => {
                    if (error instanceof RequestCancelledError) {
                        return EMPTY
                    }
                    const { AttachmentWasntUploaded } = Labels
                    if (error.text) {
                        return from(error.text()).pipe(
                            flatMap((errorMessage: string) =>
                                of(
                                    addErrorNotification(
                                        errorMessage || AttachmentWasntUploaded,
                                    ),
                                ),
                            ),
                        )
                    }
                    return of(addErrorNotification(AttachmentWasntUploaded))
                }),
            ),
        ),
    )

export const requestDeleteAttachmentEpic: Epic = (
    action$,
    state$,
    { cardView: { attachmentsAPI } },
) =>
    action$.pipe(
        ofType(actions.REQUEST_DELETE_ATTACHMENT),
        flatMap(
            ({ linkedDocumentId, itemId }: actions.RequestDeleteAttachment) => {
                const { items } = state$.value.cardView
                const itemIndex = items.findIndex((item) => item.id === itemId)

                if (itemIndex < 0) {
                    return EMPTY
                }

                return from(
                    attachmentsAPI.removeAttachment(linkedDocumentId),
                ).pipe(
                    flatMap(() =>
                        of(actions.requestAttachments(itemId, false)),
                    ),
                    catchError((error) => {
                        if (error instanceof RequestCancelledError) {
                            return EMPTY
                        }
                        return of(
                            addErrorNotification(
                                error.statusText || error.message || error,
                            ),
                        )
                    }),
                )
            },
        ),
    )

export const updateAttachmentDescriptionEpic: Epic = (
    action$,
    _state$,
    { cardView: { ipeAPI } },
) =>
    action$.pipe(
        ofType(actions.UPDATE_ATTACHMENT_DESCRIPTION),
        flatMap(
            ({
                itemId,
                objectId,
                value,
            }: actions.UpdateAttachmentDescription) =>
                from(
                    ipeAPI.saveIPE(
                        {
                            object: "ItemDocument",
                            objectId,
                            propertyId: "LinkReason",
                        },
                        value,
                    ),
                ).pipe(
                    flatMap(() =>
                        of(actions.requestAttachments(itemId, false)),
                    ),
                    catchError((error: unknown) => {
                        if (error instanceof RequestCancelledError) {
                            return EMPTY
                        }
                        console.error(error)
                        return of(
                            addErrorNotification(
                                defaultUpdatingAttachmentDescriptionErrorMessage,
                            ),
                        )
                    }),
                ),
        ),
    )

export default combineEpics(
    initializeEpic,
    reloadEpic,
    loadMoreEpic,
    requestDataEpic,
    refreshItemsEpic,
    showContextMenuEpic,
    executeContextMenuItemActionEpic,
    requestAttachmentsEpic,
    requestAddAttachmentEpic,
    requestDeleteAttachmentEpic,
    updateAttachmentDescriptionEpic,
)
